import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, Plus, X, Loader2 } from "lucide-react";

/**
 * Autocomplete picker.
 *
 * Props:
 *   placeholder
 *   suggestions: (q: string) => array of items   — sync mode (legacy)
 *                or
 *                (q: string) => Promise<array>   — async mode (catalog API)
 *   renderItem(item): JSX
 *   itemLabel(item): string for accessibility
 *   onPick(item): callback
 *   onAddCustom(query): optional — allow free-text add when nothing matches
 *   testid
 *
 * Async mode is auto-detected by inspecting the return value of the
 * suggestions function: a Promise → debounced fetch with loading state;
 * an array → original synchronous behaviour (no breaking change for
 * existing call sites that still pass the sync helpers).
 */
export default function AutocompletePicker({
  placeholder,
  suggestions,
  renderItem,
  itemLabel,
  onPick,
  onAddCustom,
  testid,
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const reqIdRef = useRef(0);

  // Sync-mode shortcut: when the function returns a synchronous array,
  // we don't need to manage state — just compute it inline. We sniff
  // this on first call; the result is a stable boolean for the lifetime
  // of the component.
  const syncResult = useMemo(() => {
    try {
      const r = suggestions(q);
      return r && typeof r.then === "function" ? null : r;
    } catch {
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestions, q]);

  const isAsync = syncResult === null;

  useEffect(() => {
    const h = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("click", h);
    return () => document.removeEventListener("click", h);
  }, []);

  // Async-mode debounced fetch
  useEffect(() => {
    if (!isAsync) return undefined;
    const myId = ++reqIdRef.current;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await suggestions(q);
        if (myId !== reqIdRef.current) return; // stale request
        setItems(Array.isArray(r) ? r : []);
      } catch {
        if (myId === reqIdRef.current) setItems([]);
      } finally {
        if (myId === reqIdRef.current) setLoading(false);
      }
    }, 180);
    return () => clearTimeout(t);
  }, [q, isAsync, suggestions]);

  const renderedItems = isAsync ? items : (syncResult || []);

  const pickAndReset = (it) => {
    onPick(it);
    setQ("");
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative" data-testid={testid}>
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#3A5275]" />
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder={placeholder}
            className="tb-input pl-9"
            data-testid={`${testid}-input`}
          />
          {loading && (
            <Loader2
              className="w-4 h-4 absolute right-9 top-1/2 -translate-y-1/2 text-[#0A3D79] animate-spin"
              data-testid={`${testid}-loading`}
            />
          )}
          {q && (
            <button type="button" onClick={() => setQ("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#3A5275]"
              aria-label="Clear" data-testid={`${testid}-clear`}>
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {onAddCustom && (
          <button type="button" onClick={() => { onAddCustom(q); setQ(""); }}
            className="btn-ghost" data-testid={`${testid}-add-custom`}>
            <Plus className="w-4 h-4" /> Add custom
          </button>
        )}
      </div>

      {open && renderedItems.length > 0 && (
        <div
          className="absolute z-50 mt-1 w-full max-h-[320px] overflow-y-auto rounded-xl bg-white shadow-[0_12px_36px_rgba(26,33,28,0.18)] border border-[#D6E3EF]"
          data-testid={`${testid}-menu`}
        >
          {renderedItems.map((it, i) => {
            // Prefer a stable identity for the list key so React preserves
            // focus/state during result-set updates: ICD-10 + description for
            // diagnoses, name for meds. Index fallback only when the item is
            // shapeless.
            const stableKey = it?.icd10
              ? `${it.icd10}::${it.description || ""}`
              : it?.name || `idx-${i}`;
            return (
              <button
                key={stableKey}
                type="button"
                onClick={() => pickAndReset(it)}
                className="w-full text-left px-3 py-2 hover:bg-[#f4f6f1] border-b border-[#E7F1FB] last:border-b-0"
                data-testid={`${testid}-item-${i}`}
                aria-label={itemLabel?.(it)}
              >
                {renderItem(it)}
              </button>
            );
          })}
        </div>
      )}

      {open && isAsync && !loading && renderedItems.length === 0 && q.trim() && (
        <div
          className="absolute z-50 mt-1 w-full px-3 py-3 rounded-xl bg-white border border-[#D6E3EF] text-[12.5px] text-[#3A5275]"
          data-testid={`${testid}-empty`}
        >
          No matches in catalog. {onAddCustom ? "Use “Add custom” to save it for next time." : ""}
        </div>
      )}
    </div>
  );
}
